import { ReactElement, useState, useEffect } from "react";
import {
  Search as SearchIcon,
  Add as AddIcon,
  FolderOpen as FolderIcon,
  FilterList as FilterIcon,
} from "@mui/icons-material";
import {
  TextField,
  Typography,
  Button,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  CircularProgress,
  Alert,
  Chip,
  FormControlLabel,
  Checkbox,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormGroup,
  Divider,
} from "@mui/material";
import type { Project } from "../services/projects.service";
import { useAuth } from "../contexts/auth.context";
import { useProjectStore } from "../stores/project.store";
import {
  DATA_TYPES,
  DATA_TYPE_LABELS,
  type DataType,
} from "../types/dataTypes";
import ResizablePanel from "./ResizablePanel.component";

export interface ProjectsSidebarProps {
  selectedProjectId?: number;
  onProjectSelect?: (project: Project) => void;
  onNewProject?: () => void;
  onProjectsChange?: (projects: Project[]) => void;
  refreshTrigger?: number;
  className?: string;
}

const ProjectsSidebar = ({
  selectedProjectId,
  onProjectSelect,
  onNewProject,
  onProjectsChange,
  refreshTrigger = 0,
  className = "",
}: ProjectsSidebarProps): ReactElement | null => {
  const { auth } = useAuth();
  const { projects, loading, error, fetchProjects } = useProjectStore();
  const [filteredProjects, setFilteredProjects] = useState<Project[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [dataTypeFilter, setDataTypeFilter] = useState<DataType[]>([]);

  // Fetch projects when auth changes or refreshTrigger changes
  useEffect(() => {
    if (auth) {
      fetchProjects(auth);
    }
  }, [auth, fetchProjects, refreshTrigger]);

  useEffect(() => {
    let filtered = projects;

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (project) =>
          project.project_name.toLowerCase().includes(query) ||
          project.project_description.toLowerCase().includes(query),
      );
    }

    if (dataTypeFilter.length > 0) {
      filtered = filtered.filter((project) =>
        dataTypeFilter.includes(project.data_type as DataType),
      );
    }

    setFilteredProjects(filtered);
  }, [searchQuery, dataTypeFilter, projects]);

  const handleProjectClick = (project: Project) => {
    onProjectSelect?.(project);
  };

  const handleNewProject = () => {
    onNewProject?.();
  };

  const handleDataTypeToggle = (dataType: DataType) => {
    setDataTypeFilter((prev) =>
      prev.includes(dataType)
        ? prev.filter((type) => type !== dataType)
        : [...prev, dataType],
    );
  };

  const handleRemoveDataType = (dataType: DataType) => {
    setDataTypeFilter((prev) => prev.filter((type) => type !== dataType));
  };

  // Projects list content
  const renderProjectsList = () => (
    <div className="flex flex-col h-full min-h-0">
      <div className="p-4 border-b border-gray-200 space-y-3">
        <TextField
          fullWidth
          placeholder="Search projects..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: <SearchIcon className="text-gray-400 mr-2" />,
          }}
          variant="outlined"
          size="small"
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: "8px",
              backgroundColor: "#f5f5f5",
              "&:hover": {
                backgroundColor: "#eeeeee",
              },
              "&.Mui-focused": {
                backgroundColor: "#ffffff",
              },
            },
          }}
        />

        <Accordion
          sx={{
            width: "100%",
            boxShadow: "none",
            "&:before": { display: "none" },
          }}
        >
          <AccordionSummary
            expandIcon={<FilterIcon />}
            sx={{
              minHeight: "40px",
              "&.Mui-expanded": { minHeight: "40px" },
              "& .MuiAccordionSummary-content": { margin: "8px 0" },
            }}
          >
            <Typography variant="body2" className="text-gray-600">
              Data Type Filter
              {dataTypeFilter.length > 0 && ` (${dataTypeFilter.length})`}
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0 }}>
            <FormGroup>
              {Object.values(DATA_TYPES).map((type) => (
                <FormControlLabel
                  key={type}
                  control={
                    <Checkbox
                      checked={dataTypeFilter.includes(type)}
                      onChange={() => handleDataTypeToggle(type)}
                      size="small"
                    />
                  }
                  label={DATA_TYPE_LABELS[type]}
                />
              ))}
            </FormGroup>
          </AccordionDetails>
        </Accordion>

        {/* Active Filters Chips */}
        {dataTypeFilter.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {dataTypeFilter.map((type) => (
              <Chip
                key={type}
                label={DATA_TYPE_LABELS[type]}
                onDelete={() => handleRemoveDataType(type)}
                size="small"
                color="primary"
                variant="outlined"
              />
            ))}
          </div>
        )}
      </div>

      {/* Projects List */}
      <div className="flex-1 overflow-y-auto min-h-0">
        {loading ? (
          <div className="flex justify-center items-center h-32">
            <CircularProgress size={24} />
          </div>
        ) : error ? (
          <Alert severity="error" className="m-4">
            {error}
          </Alert>
        ) : filteredProjects.length === 0 ? (
          <Typography variant="body2" className="p-4 text-center text-gray-500">
            {searchQuery
              ? "No projects found matching your search."
              : "No projects available."}
          </Typography>
        ) : (
          <List className="p-2">
            {filteredProjects.map((project) => (
              <ListItem key={project.id} disablePadding>
                <ListItemButton
                  onClick={() => handleProjectClick(project)}
                  selected={selectedProjectId === project.id}
                  className={`
                    rounded-lg mb-1 transition-all duration-200
                    ${
                      selectedProjectId === project.id
                        ? "bg-[#19C1DB] text-white"
                        : "hover:bg-gray-100 text-gray-700"
                    }
                  `}
                  sx={{
                    "&.Mui-selected": {
                      backgroundColor: "#19C1DB",
                      "&:hover": {
                        backgroundColor: "#17B1CB",
                      },
                    },
                  }}
                >
                  <FolderIcon
                    className="mr-3"
                    sx={{
                      fontSize: 20,
                      color:
                        selectedProjectId === project.id ? "white" : "inherit",
                    }}
                  />
                  <ListItemText
                    primary={project.project_name}
                    secondary={project.project_description}
                    primaryTypographyProps={{
                      fontSize: "0.875rem",
                      fontWeight: selectedProjectId === project.id ? 600 : 400,
                      noWrap: true,
                      title: project.project_name, // Show full name on hover
                    }}
                    secondaryTypographyProps={{
                      fontSize: "0.75rem",
                      color:
                        selectedProjectId === project.id
                          ? "rgba(255,255,255,0.7)"
                          : "text.secondary",
                      noWrap: true,
                      title: project.project_description, // Show full description on hover
                    }}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        )}
      </div>

      <Divider />
      <div className="p-4 flex-shrink-0">
        <Button
          fullWidth
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={handleNewProject}
          className="border-[#19C1DB] text-[#19C1DB] hover:bg-[#19C1DB] hover:text-white"
          sx={{ textTransform: "none" }}
        >
          New Project
        </Button>
      </div>
    </div>
  );

  // Don't render sidebar if no projects
  if (projects.length === 0) {
    return null;
  }

  return (
    <ResizablePanel
      title="Projects"
      position="left"
      minWidth={120}
      maxWidth={520}
      defaultWidth={320}
      storageKey="projectsSidebar"
      className={className}
    >
      {renderProjectsList()}
    </ResizablePanel>
  );
};

export default ProjectsSidebar;
